import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import { kpiApi } from "../api/live-client.js";
import { useKpiDefinitions } from "./useKpiDefinitions.js";
import { kpiKeys } from "./kpiQueryKeys.js";

/**
 * Resolve KPI Type (definition) ids → display names for the list + detail.
 *
 * The browsable catalog (`GET /v1/kpi-definitions`, via {@link useKpiDefinitions})
 * deliberately omits `compatibility`-provenance (migration-backfilled) and
 * non-`active` Types — but configured KPIs legitimately reference exactly those
 * (every pre-split KPI points at a hidden compatibility Type). So any id absent
 * from the catalog is dereferenced individually via `getDefinition`
 * (`GET /v1/kpi-definitions/{id}`, which resolves any provenance). Returns a
 * `(definitionId) => name` lookup; a still-unresolved id falls back to "—".
 */
export function useDefinitionNames(
  definitionIds: ReadonlyArray<string | null>,
): (definitionId: string | null) => string {
  const config = useApiClient();
  const catalog = useKpiDefinitions();

  const catalogById = useMemo(
    () =>
      new Map(
        (catalog.data ?? []).map((d) => [d.definitionId, d.name] as const),
      ),
    [catalog.data],
  );

  // Stable signature of the requested ids so the by-id query set only changes
  // when the distinct id set does, not on every render.
  const idsSignature = useMemo(
    () =>
      [...new Set(definitionIds.filter((id): id is string => Boolean(id)))]
        .sort()
        .join(","),
    [definitionIds],
  );

  // Ids referenced by KPIs but hidden from the browsable catalog — resolve each
  // by id (rare; caches at the same 5-minute staleTime as the catalog). Gated on
  // the catalog having loaded: until then every id looks "missing", which would
  // fire a needless getDefinition per id on mount.
  const missingIds = useMemo(
    () =>
      catalog.data && idsSignature
        ? idsSignature.split(",").filter((id) => !catalogById.has(id))
        : [],
    [catalog.data, idsSignature, catalogById],
  );

  const byIdQueries = useQueries({
    queries: missingIds.map((id) => ({
      queryKey: kpiKeys.definition(id),
      queryFn: () => kpiApi.getDefinition(config, id),
      staleTime: 5 * 60_000,
    })),
  });

  const resolvedById = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of byIdQueries) {
      if (q.data) map.set(q.data.definitionId, q.data.name);
    }
    return map;
  }, [byIdQueries]);

  return useMemo(
    () =>
      (definitionId: string | null): string =>
        (definitionId &&
          (catalogById.get(definitionId) ?? resolvedById.get(definitionId))) ||
        "—",
    [catalogById, resolvedById],
  );
}
